import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, Select, FormControl,
  InputLabel, FormControlLabel, Checkbox, Chip, Autocomplete, Alert,
  CircularProgress, Divider, IconButton, Tooltip, List, ListItem,
  ListItemIcon, ListItemText,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  AttachFile as AttachIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { KB_QUILL_MODULES, KB_QUILL_FORMATS } from '../utils/kbQuill';
import apiService from '../services/api';

interface Category { id: string; name: string; }
interface Tag { id: string; name: string; slug: string; }
interface Attachment { id: string; display_name: string; filename: string; content_type: string; file_size_bytes: number | null; }

function fmtBytes(n: number | null) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function KnowledgeBaseEditPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form fields
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [status, setStatus] = useState('draft');
  const [isPublic, setIsPublic] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  // Options
  const [categories, setCategories] = useState<Category[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);

  // Attachment upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    apiService.kbListCategories().then(setCategories).catch(() => {});
    apiService.kbListTags().then(setAllTags).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    apiService.kbGetArticle(id).then((a) => {
      setTitle(a.title || '');
      setContent(a.content || '');
      setExcerpt(a.excerpt || '');
      setStatus(a.status || 'draft');
      setIsPublic(a.is_public || false);
      setCategoryId(a.category_id || '');
      setExpiresAt(a.expires_at ? a.expires_at.slice(0, 16) : '');
      setSelectedTags(a.tags || []);
      setAttachments(a.attachments || []);
    }).catch(() => setError('Failed to load article')).finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleSave = async () => {
    if (!title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError('');
    try {
      const tagIds = selectedTags.map((t) => t.id).join(',');
      const payload = {
        title: title.trim(),
        content,
        excerpt: excerpt.trim(),
        status,
        is_public: isPublic,
        category_id: categoryId || undefined,
        expires_at: expiresAt || undefined,
        tag_ids: tagIds,
      };
      let saved: any;
      if (isEdit && id) {
        saved = await apiService.kbUpdateArticle(id, { ...payload, category_id: categoryId || null, expires_at: expiresAt || null });
        setSuccess('Article saved');
        setAttachments(saved.attachments || []);
      } else {
        saved = await apiService.kbCreateArticle(payload);
        setSuccess('Article created');
        navigate(`/knowledge-base/${saved.id}/edit`, { replace: true });
      }
    } catch {
      setError('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    setCreatingTag(true);
    try {
      const tag = await apiService.kbCreateTag(newTagName.trim());
      setAllTags((prev) => [...prev, tag].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedTags((prev) => [...prev, tag]);
      setNewTagName('');
    } catch {
      setError('Failed to create tag');
    } finally {
      setCreatingTag(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!id) { setError('Save the article first before adding attachments'); return; }
    setUploading(true);
    try {
      const att = await apiService.kbUploadAttachment(id, file, file.name);
      setAttachments((prev) => [...prev, att]);
    } catch {
      setError('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attId: string) => {
    if (!id || !window.confirm('Remove this attachment?')) return;
    try {
      await apiService.kbDeleteAttachment(id, attId);
      setAttachments((prev) => prev.filter((a) => a.id !== attId));
    } catch {
      setError('Failed to delete attachment');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" gap={1} mb={3} alignItems="center">
        <Button startIcon={<BackIcon />} onClick={() => navigate('/knowledge-base')}>Back</Button>
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
          {isEdit ? 'Edit Article' : 'New Article'}
        </Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Save
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <TextField
          label="Title"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
          required
        />
        <TextField
          label="Excerpt / Summary"
          fullWidth
          multiline
          rows={2}
          value={excerpt}
          onChange={(e) => setExcerpt(e.target.value)}
          sx={{ mb: 2 }}
          helperText="Optional short description shown in the article list"
        />

        {/* Article content */}
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Content</Typography>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            mb: 3,
            '& .ql-container': { minHeight: 300, fontSize: 15 },
            '& .ql-editor': { minHeight: 300 },
          }}
        >
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            modules={KB_QUILL_MODULES}
            formats={KB_QUILL_FORMATS}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Metadata row */}
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select value={categoryId} label="Category" onChange={(e) => setCategoryId(e.target.value)}>
              <MenuItem value="">— No category —</MenuItem>
              {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField
            label="Expiry date/time"
            type="datetime-local"
            size="small"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Leave blank for no expiry"
            sx={{ minWidth: 220 }}
          />

          <FormControlLabel
            control={<Checkbox checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
            label="Mark as public (future customer KB)"
            sx={{ alignSelf: 'center' }}
          />
        </Box>

        {/* Tags */}
        <Typography variant="subtitle2" gutterBottom>Tags</Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
          <Autocomplete
            multiple
            size="small"
            options={allTags}
            getOptionLabel={(o) => o.name}
            value={selectedTags}
            onChange={(_, v) => setSelectedTags(v)}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option.name} size="small" {...getTagProps({ index })} key={option.id} />
              ))
            }
            renderInput={(params) => <TextField {...params} label="Tags" />}
            sx={{ minWidth: 300 }}
          />
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              size="small"
              placeholder="New tag name"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateTag(); } }}
              sx={{ width: 160 }}
            />
            <Button size="small" startIcon={<AddIcon />} onClick={handleCreateTag} disabled={creatingTag || !newTagName.trim()}>
              Add
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Attachments */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Attachments</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={uploading ? <CircularProgress size={14} /> : <AttachIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !id}
          >
            Upload File
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); e.target.value = ''; }}
          />
        </Box>
        {!id && (
          <Alert severity="info">Save the article first to enable attachment uploads.</Alert>
        )}
        {attachments.length === 0 && id && (
          <Typography variant="body2" color="text.secondary">No attachments yet.</Typography>
        )}
        <List dense>
          {attachments.map((att) => (
            <ListItem key={att.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><AttachIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary={att.display_name} secondary={fmtBytes(att.file_size_bytes)} />
              <Tooltip title="Download">
                <IconButton size="small" component="a" href={apiService.kbAttachmentDownloadUrl(id!, att.id)} download>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton size="small" color="error" onClick={() => handleDeleteAttachment(att.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
}
